/**
 * Resolves the in-app route for the entity an Important Date (or any child
 * record) hangs off.
 *
 * This used to be four hand-copied `routeMap` literals — in the critical-date
 * dialog, the Important Dates grid column, the date list, and the notification
 * email. They drifted: two of them were missing `party` and `sites`, so a date
 * attached to a Party or a Site rendered no "Link To Source" link, offered a
 * "Go To Item" button that silently did nothing, and went out in email with no
 * link at all. Nothing failed loudly, because a miss just yields ''.
 *
 * One map, one lookup. Add a type here and every surface picks it up.
 */

/**
 * Keys are LOWERCASED `entityType` values as they actually appear on records —
 * which is not the tidy set you'd guess:
 *
 *  - `EntityType.Party` is `'party'` but the route is `vendor`.
 *  - `EntityType.Site` is `'sites'` (plural) but the route is `site` (singular).
 *  - Callers also pass raw strings: contract-form writes `'Contract'`, and old
 *    records carry spellings like `'legalEntity'`.
 *
 * So this maps observed data to routes, and both spellings stay where they
 * differ. Values must match a real path in app.routes.ts.
 */
const ROUTE_BY_ENTITY_TYPE: Readonly<Record<string, string>> = {
  'contract': 'contract',
  'party': 'vendor',
  'vendor': 'vendor',
  'person': 'person',
  'site': 'site',
  'sites': 'site',
  'legal entity': 'legal-entity',
  'legalentity': 'legal-entity',
  'baa': 'baa',
  'coi': 'coi',
  'provision': 'provision',
  'docreview': 'doc-review',
  'document review': 'doc-review'
};

/** The route segment for an entity type, or '' if we don't have a view for it. */
export function routeSegmentForEntityType(entityType: string | null | undefined): string {
  if (!entityType) return '';
  return ROUTE_BY_ENTITY_TYPE[entityType.toLowerCase()] || '';
}

/**
 * Origin-independent path to an entity's detail view, e.g.
 * `/securehome/vendor/27-0756`. Returns '' when the type is unknown or the key
 * is missing — callers must treat '' as "no link", never as a path to follow:
 * the bare deployment root redirects to My Work, which is exactly the wrong
 * place to send someone who clicked a link to a specific record.
 */
export function entityPathFor(
  entityType: string | null | undefined,
  entityKey: string | null | undefined
): string {
  const route = routeSegmentForEntityType(entityType);
  if (!route || !entityKey) return '';
  return `/securehome/${route}/${entityKey}`;
}

/**
 * Whether we can actually navigate to this record's parent. Use this to decide
 * whether to OFFER a link — checking only that entityType/key are non-empty
 * promises a destination we may not have, which is how the date list ended up
 * showing a "Go To Item" button that did nothing.
 */
export function hasEntityRoute(
  entityType: string | null | undefined,
  entityKey: string | null | undefined
): boolean {
  return entityPathFor(entityType, entityKey) !== '';
}
